import { httpClient } from '@/api/httpClient';
import {
  mapApiResourceToFile,
  mapApiResourceToFileUploadInit,
  mapFileUploadInitPayload,
  type FileApiResource,
  type FileUploadInit,
  type FileUploadInitApiResource,
  type FileUploadInitPayload,
  type PatientFile,
} from './filesApi.types';

export async function initiatePatientFileUpload(
  patientId: string,
  payload: FileUploadInitPayload
): Promise<FileUploadInit> {
  const response = await httpClient.post<FileUploadInitApiResource>(
    '/patients/' + patientId + '/files',
    mapFileUploadInitPayload(payload)
  );
  return mapApiResourceToFileUploadInit(response.data);
}

export async function fetchPatientFiles(patientId: string): Promise<PatientFile[]> {
  const response = await httpClient.get<FileApiResource[]>('/patients/' + patientId + '/files');
  return response.data.map(mapApiResourceToFile);
}

export async function completePatientFileUpload(fileId: string): Promise<PatientFile> {
  const response = await httpClient.post<FileApiResource>('/files/' + fileId + '/complete');
  return mapApiResourceToFile(response.data);
}

export async function setPatientFileVisibility(
  fileId: string,
  visibility: string
): Promise<PatientFile> {
  const response = await httpClient.patch<FileApiResource>('/files/' + fileId + '/visibility', {
    visibility,
  });
  return mapApiResourceToFile(response.data);
}

export async function approvePatientFile(fileId: string): Promise<PatientFile> {
  const response = await httpClient.post<FileApiResource>('/files/' + fileId + '/approve');
  return mapApiResourceToFile(response.data);
}

export async function rejectPatientFile(
  fileId: string,
  reviewNote: string | null
): Promise<PatientFile> {
  const response = await httpClient.post<FileApiResource>('/files/' + fileId + '/reject', {
    review_note: reviewNote,
  });
  return mapApiResourceToFile(response.data);
}

export async function fetchPatientFileAttachmentUrl(fileId: string): Promise<string> {
  const response = await httpClient.get<{ download_url: string }>(
    '/files/' + fileId + '/download-url',
    { params: { disposition: 'attachment' } }
  );
  return response.data.download_url;
}

export async function fetchOwnPatientFileAttachmentUrl(fileId: string): Promise<string> {
  const response = await httpClient.get<{ download_url: string }>(
    '/me/files/' + fileId + '/download-url'
  );
  return response.data.download_url;
}

export async function initiateOwnPatientFileUpload(
  patientId: string,
  payload: FileUploadInitPayload
): Promise<FileUploadInit> {
  const response = await httpClient.post<FileUploadInitApiResource>(
    '/me/patients/' + patientId + '/files',
    mapFileUploadInitPayload(payload)
  );
  return mapApiResourceToFileUploadInit(response.data);
}

export async function fetchOwnPatientFiles(patientId: string): Promise<PatientFile[]> {
  const response = await httpClient.get<FileApiResource[]>(
    '/me/patients/' + patientId + '/files'
  );
  return response.data.map(mapApiResourceToFile);
}

export async function completeOwnPatientFileUpload(fileId: string): Promise<PatientFile> {
  const response = await httpClient.post<FileApiResource>('/me/files/' + fileId + '/complete');
  return mapApiResourceToFile(response.data);
}

export async function uploadFileToPresignedUrl(
  uploadUrl: string,
  file: File,
  contentType: string
): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body: file,
  });
  if (response.ok === false) {
    throw new Error('File upload to storage failed with status ' + response.status);
  }
}
